import _ from 'lodash';
import { rgba } from 'polished';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useIsSupport, useWorkspaceConnector } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Avatar,
	Button,
	Empty,
	Heading3,
	Paragraph,
	Popover,
	Skeleton,
	tokens,
} from '@treams/treams-ui';

import Preview from '../../../goals/group-selector/Preview';

const Name = styled(Box)`
	font-weight: bold;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const Footnote = styled(Paragraph)`
	font-size: 12px;
	line-height: 15px;
`;

type ContainerProps = {
	$color: string;
	$selected?: boolean;
	$enabled?: boolean;
};

const Container = styled(Flex)<ContainerProps>`
	border-radius: 5px;
	background: ${(props) => props.theme.colors.white};
	box-shadow:
		0px 0px 0px 1px ${rgba(tokens.text.secondary, 0.25)},
		0px 2px 4px ${rgba(tokens.text.secondary, 0.25)};
	width: 180px;
	height: 48px;
	padding: 0px 10px 0px 4px;
	font-size: 14px;
	line-height: 14px;
	align-items: center;
	border-left: 4px solid ${(props) => props.theme.colors[props.$color]};

	${(props) =>
		props.$enabled !== false &&
		`
	cursor: pointer;

	&:hover {
		box-shadow: 0px 0px 0px 1px ${rgba(tokens.text.secondary, 0.5)},
			0px 2px 4px ${rgba(tokens.text.secondary, 0.25)};
	}`}

	${(props) =>
		props.$selected &&
		`
		${Footnote} {
			color: ${rgba(props.theme.colors.white, 0.5)};
		}
		
		&, &:hover {
			background: ${props.theme.colors.primary};
			color: ${props.theme.colors.white};
			border-left-color: ${rgba(props.theme.colors.white, 0.5)};
			box-shadow: 0px 0px 0px 2px ${rgba(props.theme.colors.primary, 0.25)};
		}
	`}
`;

const Main = styled(Box)`
	border-right: 1px solid ${tokens.border.subtle};
`;

const Grid = styled(Flex)`
	padding: 16px;
	border-radius: 5px;
	border: 1px solid ${tokens.border.subtle};
	align-items: center;
	flex-direction: column;
`;

type Props = ({ user: graphql.User } | { group: graphql.Group }) & {
	transitive?: boolean;
	inverse?: boolean;
	indirect?: boolean;
	me: graphql.User;
	role: graphql.Role;
	externalReferenceID?: string;
	refetch: () => Promise<unknown>;
};

export default function Role(props: Props) {
	const { t } = useTranslation();
	const connector = useWorkspaceConnector();
	const connectorTitle = connector
		? t(`connectors.product.${connector.toLowerCase()}.title`)
		: undefined;

	return 'user' in props ? (
		<UserRole
			transitive={props.transitive}
			indirect={props.indirect}
			inverse={props.inverse}
			me={props.me}
			user={props.user}
			role={props.role}
			refetch={props.refetch}
			externalReferenceID={props.externalReferenceID}
			connector={connectorTitle}
		/>
	) : (
		<GroupRole
			indirect={props.indirect}
			me={props.me}
			role={props.role}
			refetch={props.refetch}
			group={props.group}
			externalReferenceID={props.externalReferenceID}
			connector={connectorTitle}
		/>
	);
}

type UserRolePopoverProps = {
	source: graphql.User;
	target: graphql.User;
	role: graphql.Role;
	refetch: () => Promise<unknown>;
	externalReferenceID?: string;
	indirect?: boolean;
	transitive?: boolean;
	connector?: string;
};

const UserRolePopover: FC<UserRolePopoverProps> = ({
	source,
	target,
	role,
	refetch,
	externalReferenceID,
	indirect,
	transitive,
	connector,
}: UserRolePopoverProps) => {
	const { t } = useTranslation();
	const [revoke] = graphql.useRevokeRoleMutation();
	const isSupport = useIsSupport();

	const enableRevoke = !externalReferenceID || isSupport;

	const revokeRole = async () => {
		await revoke({
			variables: {
				input: {
					userID: source.id,
					roleID: role.id,
					targetID: target.id,
					targetType: graphql.RoleTargetType.User,
				},
			},
		});

		await refetch();
	};

	return (
		<Box p={3} width={300}>
			<Heading3 mb={0}>{t(`roles.${role.id}`)}</Heading3>
			<Paragraph mb={1}>
				{t(`roles.explanations.${_.camelCase(role.id)}`, {
					name: source.firstName,
					target: target.firstName,
				})}
			</Paragraph>

			{indirect && (
				<Paragraph mb={1}>
					{t(`roles.explanations.transitive.suffix`, { target: target.firstName })}
				</Paragraph>
			)}
			{transitive && (
				<Grid mt={2}>
					<Empty
						description={t(`employees.settings.roles.revokeImpossible`, {
							name: target.firstName,
						})}
					/>
				</Grid>
			)}

			{externalReferenceID && (
				<Grid mt={2}>
					<Empty
						description={t(`roles.explanations.external`, {
							connector,
						})}
					/>
				</Grid>
			)}

			{!externalReferenceID && connector && (
				<Grid mt={2}>
					<Empty
						description={t(`roles.explanations.internal`, {
							connector,
						})}
					/>
				</Grid>
			)}

			{!transitive && enableRevoke && (
				<Flex>
					<Button
						mt={2}
						type="destructive"
						title={t('employees.settings.roles.revokeButton')}
						small
						onClick={revokeRole}
					/>
				</Flex>
			)}
		</Box>
	);
};

type UserRoleProps = {
	me: graphql.User;
	role: graphql.Role;
	user: graphql.User;
	externalReferenceID?: string;
	refetch: () => Promise<unknown>;
	connector?: string;
	transitive?: boolean;
	indirect?: boolean;
	inverse?: boolean;
} & ({ user: graphql.User } | { group: graphql.Group });

const UserRole: FC<UserRoleProps> = ({
	me,
	role,
	user,
	externalReferenceID,
	refetch,
	connector,
	transitive,
	indirect,
	inverse,
}: UserRoleProps) => {
	const { t } = useTranslation();

	const [visible, setVisible] = useState(false);

	return (
		<Popover.Target
			visible={visible}
			onDismiss={() => setVisible(false)}
			contents={
				<UserRolePopover
					transitive={transitive}
					indirect={indirect}
					source={inverse ? user : me}
					target={inverse ? me : user}
					role={role}
					refetch={refetch}
					externalReferenceID={externalReferenceID}
					connector={connector}
				/>
			}
		>
			<Container onClick={() => setVisible(true)} $color="employee" $selected={visible}>
				<Avatar user={user} size={32} mr={2} />
				<Box>
					<Flex justifyContent="space-between">
						<Name>
							{user.firstName} {user.lastName}
						</Name>
					</Flex>
					<Footnote>
						{t(`roles.${role.id}`)}
						{(indirect || transitive) && '*'}
					</Footnote>
				</Box>
			</Container>
		</Popover.Target>
	);
};

type GroupRoleProps = {
	me: graphql.User;
	role: graphql.Role;
	group: graphql.Group;
	externalReferenceID?: string;
	refetch: () => Promise<unknown>;
	indirect?: boolean;
	connector?: string;
};

const GroupRolePopover: FC<GroupRoleProps> = ({
	me,
	role,
	group,
	externalReferenceID,
	refetch,
	indirect,
	connector,
}: GroupRoleProps) => {
	const { t } = useTranslation();
	const isSupport = useIsSupport();

	const enableRevoke = !externalReferenceID || isSupport;

	const [revoke] = graphql.useRevokeRoleMutation();

	const revokeRole = async () => {
		await revoke({
			variables: {
				input: {
					userID: me.id,
					roleID: role.id,
					targetID: group.id,
					targetType:
						group.type === graphql.GroupType.Department
							? graphql.RoleTargetType.Department
							: graphql.RoleTargetType.Team,
				},
			},
		});

		await refetch();
	};

	return (
		<Flex>
			<Main p={3} width={240}>
				<Heading3 mb={2}>{t(`roles.${role.id}`)}</Heading3>
				<Paragraph mb={3}>
					{t(`roles.explanations.${_.camelCase(role.id)}`, {
						name: me.firstName,
						target: group.name,
					})}
				</Paragraph>
				{indirect && (
					<Paragraph mb={3}>
						{t(`roles.explanations.transitive.suffix`, { target: group.name })}
					</Paragraph>
				)}

				{externalReferenceID && (
					<Grid mt={2}>
						<Empty
							description={t(`roles.explanations.external`, {
								connector,
							})}
						/>
					</Grid>
				)}

				{!externalReferenceID && connector && (
					<Grid mt={2}>
						<Empty
							description={t(`roles.explanations.internal`, {
								connector,
							})}
						/>
					</Grid>
				)}

				{enableRevoke && (
					<Flex>
						<Button
							mt={2}
							type="destructive"
							title={t('employees.settings.roles.revokeButton')}
							small
							onClick={revokeRole}
						/>
					</Flex>
				)}
			</Main>
			<Preview group={group} />
		</Flex>
	);
};

const GroupRole: FC<GroupRoleProps> = ({
	me,
	role,
	group,
	externalReferenceID,
	refetch,
	indirect,
	connector,
}: GroupRoleProps) => {
	const { t } = useTranslation();
	const [visible, setVisible] = useState(false);

	return (
		<Popover.Target
			visible={visible}
			onDismiss={() => setVisible(false)}
			contents={
				<GroupRolePopover
					indirect={indirect}
					me={me}
					role={role}
					group={group}
					refetch={refetch}
					externalReferenceID={externalReferenceID}
					connector={connector}
				/>
			}
		>
			<Container
				role="button"
				onClick={() => setVisible(true)}
				$color="manager"
				$selected={visible}
			>
				<Box ml={2}>
					<Name>{group.name}</Name>
					<Footnote>
						{t(`roles.${role.id}`)}
						{indirect && '*'}
					</Footnote>
				</Box>
			</Container>
		</Popover.Target>
	);
};

function RoleSkeleton() {
	return <Skeleton.Box width={180} height={48} />;
}

Role.Skeleton = RoleSkeleton;
